import React, { useState, useMemo, useRef, useEffect } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCSwitch, RCCTooltip, RCCSelectOptionsCard } from "react-common-components";
import { useForm } from "react-hook-form";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ConfigsContainer from "src/components/ConfigsContainer";
import Title from "src/components/Title";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import formatDate from "src/utils/formatDate";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import ButtonScrollTo from "src/components/ButtonScrollTo";
import { IRefExportListComponent } from "src/components/List/interface";
import downloadFile from "src/utils/downloadFile";
import ExportListContainer from "src/components/List/exportList";
import { useAuth } from "src/context/Auth/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import LovCoins from "src/components/Lov/Coins";
import useDragHorizontal from "src/hooks/useDragHorizontal";
import { BudgetType, IConfigsConsultValues, IDataTable } from "./interface";
import ConfigsOptions from "./components/ConfigsOptions";
import getMethodName from "./getMethodName";
import ListTable from "./components/List";
import { IOftbp001 } from "../Panel/components/Modals/interface";
import * as ListStyles from "./components/List/styles";

const ConsultValues: React.FC = () => {
	const params = useParams<{
        budgetPlanId: string;
    }>();
	
	const divRef = useRef<HTMLDivElement>(null);
	const { onMouseDown } = useDragHorizontal(divRef, true);

	const { administrator, superUser } = useAuth();
	const history = useHistory();
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();
	
	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const [budgetTypeSelected, setBudgetTypeSelected] = useState<BudgetType>(objQueryParams?.budgetType as BudgetType || "DO");
	const [financialPlanTitle] = useState(objQueryParams?.name || "");
	const [subsidiaryTitle, setSubsidiaryTitle] = useState<string | undefined>(undefined);

	const [showConfigs, setShowConfigs] = useState(false);
	const [configs, setConfigs] = useState<IConfigsConsultValues>({ 
		divider: "1",
		listItens: "V",
		showColumnLastYear: "S",
		showColumnPlanned: "S",
		showColumnRealized: "S",
		showPeriods: "T",
		showVariationValue: "N",
		showVariationPercent: "S",
		variation: "PR",
		coinPlanned: null,
		coinPlannedInitials: null,
		coinRealized: null,
		coinRealizedInitials: null
	});
	const optBudgetType = [{
		label: translate("Despesas operacionais"),
		value: BudgetType.OPERATIONAL_EXPENSES
	},{
		label: translate("Outras despesas"),
		value: BudgetType.OTHER_EXPENSES
	},{
		label: translate("Receita de vendas"),
		value: BudgetType.SALES_REVENUE
	}];
	const [options, setOptions] = useState<{label: string; value: BudgetType}[]>([]);
	const { register : registerConfigs, control: controlConfigs } = useForm();


	const { handleSubmit : handleSubmitFormOptions, register : registerFormOptions, control: controlFormOptions, setValue: setValueFormOptions } = useForm();
	const submitFormOptions = async (data: any) => {
		const dataForm = data;	
		loadData({...dataForm, ...configs});
	};

	const [dataTable, setDataTable] = useState<IDataTable[] | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [errorLoadingData, setErrorLoadingData] = useState(false);
	const [notAllowedData, setNotAllowedData] = useState(false);
	const [hasConversionValueEqualZero, setHasConversionValueEqualZero] = useState(false);
	const loadData = async (dataForm: any) => {
		if(!params.budgetPlanId)
			return;
			
		try{
			setLoading(true);
			setErrorLoadingData(false);
			setNotAllowedData(false);
			const methodName = getMethodName(budgetTypeSelected);

			let aditionalParams = {};
			if(budgetTypeSelected === BudgetType.OPERATIONAL_EXPENSES)
				aditionalParams = {...aditionalParams, showCorporativeOPValues: dataForm.showCorporativeOPValues === "S"};
			if(budgetTypeSelected === BudgetType.SALES_REVENUE)
				aditionalParams = {...aditionalParams, show: dataForm.show };

			const {data} = await RCCServicesApi.getRequest({
				method: `FinancialManagement/${methodName}`,
				urlParameters: {
					ofa001Id: params.budgetPlanId,
					group: typeof dataForm.group === "object" ? dataForm.group.value : dataForm.group,
					subsidiary: dataForm.subsidiary || "",
					lov1: dataForm.lov1 || "",
					lov2: dataForm.lov2 || "",
					startDate: dataForm.startDate ? formatDate(dataForm.startDate) : "",
					endDate: dataForm.endDate ? formatDate(dataForm.endDate) : "",
					showLevelBelow: dataForm.showLevelBelow,
					showHierarchy: dataForm.showHierarchy,
					divider: dataForm.divider,
					listAllItens: dataForm.listItens === "T",
					fetchLastYear: dataForm.showColumnLastYear === "S" || dataForm.showVariationValue === "S" || dataForm.showVariationPercent === "S",
					variation: dataForm.showVariationValue || dataForm.showVariationPercent ? dataForm.variation : "",
					coinPlanned: coinConversion && dataForm.coinPlanned && dataForm.coinPlanned !== null ? dataForm.coinPlanned.toString() : "",
					coinRealized: coinConversion && dataForm.coinRealized && dataForm.coinRealized !== null ? dataForm.coinRealized.toString() : "",
					...aditionalParams
				}
			});
			setHasConversionValueEqualZero(data.hasConversionValueEqualZero);
			setDataTable(data.list);
			setLoading(false);
		}catch(err: any){
			if(err.response && err.response.data && err.response.data.Message === "Você não tem permissão para a hierarquia solicitada"){
				setNotAllowedData(true);
			}else{
				setErrorLoadingData(true);
				standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			}
			setLoading(false);
		}
	};

	const [loadingExport, setLoadingExport] = useState(false);
	const exportListContainerRef = useRef<IRefExportListComponent>(null);
	const exportStructure = async () => {
		const data = exportListContainerRef.current?.executeExport();

		if(!data || data.length === 0){
			addToast({
				title: translate("Erro"),
				description: translate("Não há valores para exportar"),
				color: "yellow"
			});
			return;	
		}

		try{	
			setLoadingExport(true);

			downloadFile(data.join("\n"), `${translate("Consulta valores de orçamento")} - ${financialPlanTitle}.csv`, true);

			addToast({
				color: "green",
				description: translate("Estrutura exportada com sucesso"),
				title: translate("Sucesso")
			});

			setLoadingExport(false);
		}catch(err){
			setLoadingExport(false);
			standardErrorMessages(err, translate("Erro ao exportar estrutura valores"), addToast);
		}
	};

	const [corporativeValueOutdated, setCorporativeValueOutdated] = useState(false);
	const [budgetUsesApportionment, setBudgetUsesApportionment] = useState(false);
	const [coinConversion, setCoinConversion] = useState(false);
	const fetchIfBudgetUsesApportionment = async () => {
		if(!params.budgetPlanId)
			return;
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/GetConfig",
				urlParameters: {
					budgetId: params.budgetPlanId,
					origin: "5"
				}
			});
			setBudgetUsesApportionment(data.budget.off001Usarateiocc === "S");
			setCorporativeValueOutdated(data.budget.off001Usarateiocc === "S" && data.budgetPlan?.ofa001Vlrcorpatu === "N");
			setCoinConversion(data.budgetPlan?.ofa001Utilizamoeda === "S");
		}catch(err){
			setBudgetUsesApportionment(false);
			setCorporativeValueOutdated(false);
		}
	};
	const updateCorporativeValues = () => {
		confirm(
			translate("Atualizar valores corporativos"), 
			`${translate("Esse processo pode demorar alguns minutos, deseja continuar")}?`,
			async () => {
				if(!params.budgetPlanId)
					return;

				await RCCServicesApi.putRequest({
					method: "FinancialManagement/UpdateCorporativeValues",
					urlParameters: {
						budgetPlanId: params.budgetPlanId
					}
				});

				setCorporativeValueOutdated(false);

				addToast({
					color: "green",
					description: translate("Valores corporativos atualizados com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao atualizar valores corporativos"), addToast);
			},
			false
		);
	};


	const Table = useMemo(() => {
		if(loading)
			return <Loader />;
		if(notAllowedData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Você não tem permissão para a hierarquia solicitada")}
			/>;
		if(errorLoadingData)
			return  <RCCAlert 
				title={translate("Atenção")}
				description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
			/>;
		
		if(dataTable){
			if(dataTable.length === 0)
				return <RCCAlert 
					title={translate("Atenção")}
					description={translate("Não foram encontrados dados")}
				/>;

			return <ListStyles.DivFlexMaxWidth ref={divRef}><ListTable 
				data={dataTable} 
				showOnlyAccumulated={configs.showPeriods === "A"}
				showColumnLastYear={configs.showColumnLastYear === "S"}
				showColumnPlanned={configs.showColumnPlanned === "S"}
				showColumnRealized={configs.showColumnRealized === "S"}
				showVariationValue={configs.showVariationValue === "S"}
				showVariationPercent={configs.showVariationPercent === "S"}
				isExpense={budgetTypeSelected === BudgetType.OPERATIONAL_EXPENSES || budgetTypeSelected === BudgetType.OTHER_EXPENSES}
				title={optBudgetType.find((x) => x.value === budgetTypeSelected)?.label}
				subsidiary={subsidiaryTitle}
				offsetLeft={0}
				coinPlannedInitials={configs.coinPlannedInitials !== null ? configs.coinPlannedInitials : undefined}
				coinRealizedInitials={configs.coinRealizedInitials !== null ? configs.coinRealizedInitials : undefined}
				onMouseDown={onMouseDown}
			/></ListStyles.DivFlexMaxWidth>;
		}
			
		return <></>;
	}, [loading, errorLoadingData, notAllowedData, dataTable]);

	const [loadingPermissions, setLoadingPermissions] = useState(true);
	const loadPermissionsData = async () => {
		if(administrator || superUser){
			setOptions(optBudgetType);
			setLoadingPermissions(false);
			return;
		}
		try{
			setLoadingPermissions(true);

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/SelectBudgetPlanPermissions",
				urlParameters: {
					budgetPlanId: params.budgetPlanId
				}
			});
			if(administrator || superUser || data.find((x: IOftbp001) => x.ofp001Permissao === 1 || x.ofp001Permissao === 3))
				setOptions(optBudgetType);
			else{
				const optionsArray = [];
				if(data.find((x: IOftbp001) => (x.ofp001Permissao === 2 && x.ofp001Acessodo === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessodo === "S")))
					optionsArray.push({
						label: translate("Despesas operacionais"),
						value: BudgetType.OPERATIONAL_EXPENSES
					});
				if(data.find((x: IOftbp001) => (x.ofp001Permissao === 2 && x.ofp001Acessoou === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessoou === "S")))
					optionsArray.push({
						label: translate("Outras despesas"),
						value: BudgetType.OTHER_EXPENSES
					});
				if(data.find((x: IOftbp001) => (x.ofp001Permissao === 2 && x.ofp001Acessore === "S") || (x.ofp001Permissao === 4 && x.ofp001Acessore === "S")))
					optionsArray.push({
						label: translate("Receita de vendas"),
						value: BudgetType.SALES_REVENUE
					});
				if(optionsArray.length > 0)
					setBudgetTypeSelected(objQueryParams?.budgetType ? optionsArray.find((x) => x.value === objQueryParams?.budgetType)?.value ?? optionsArray[0].value : optionsArray[0].value);
				setOptions(optionsArray);
			}
			setLoadingPermissions(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			setLoadingPermissions(false);
		}
	};

	const yearConsult = () => {
		if(objQueryParams?.year)
			return Number(objQueryParams.year);
		if(objQueryParams?.startYear && objQueryParams.endYear){
			const actualYear = new Date().getFullYear();
			if((actualYear >= Number(objQueryParams.startYear)) && (actualYear <= Number(objQueryParams.endYear)))
				return actualYear;
			return Number(objQueryParams.startYear);
		}
		return new Date().getFullYear();
	}; 
	
	useEffect(() => {
		loadPermissionsData();
		fetchIfBudgetUsesApportionment();
	}, []);

	if(loadingPermissions)
		return <Loader />;

	if(options.length === 0)
		return <RCCAlert 
			title={translate("Atenção")}
			description={translate("Sem acesso")}
		/>;

	return (
		<>

			{/* Header -> Results + Buttons */}
			<PageTitle
				title={`${translate("Consulta valores de orçamento")} - ${financialPlanTitle}`}
				icon={{
					name: "list"
				}}
			>
				{budgetTypeSelected === BudgetType.OPERATIONAL_EXPENSES && corporativeValueOutdated && (
					<RCCTooltip
						text={translate("Valores corporativos desatualizados")}
						color="yellow"
					>
						<RCCButton
							icon={{name: "exclamationTriangle", color: "#FFF"}}
							color="yellow"
							layout="isRounded"
							size="md"
							onClick={updateCorporativeValues}
						/>
					</RCCTooltip>
				)}
				<RCCTooltip
					text={translate("Configurações")}
					color="blue"
				>
					<RCCButton
						icon={{name: "settings", color: "#FFF", size: 16}}
						color="blue"
						layout="isRounded"
						size="md"
						onClick={() => setShowConfigs((prevState) => !prevState)}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Voltar")}
					color="grey"
				>
					<RCCButton
						icon={{name: "arrowBack", color: "#FFF"}}
						color="grey"
						layout="isRounded"
						size="md"
						onClick={() => history.push(`/financialmanagement/panel/${params.budgetPlanId}`)}
					/>
				</RCCTooltip>
			</PageTitle>

			{/* Configs */}
			<ConfigsContainer
				showConfigs={showConfigs}
				showClearConfigs={false}
				showSubmitConfigs={false}
			>
				<RCCGrid
					container
					item
					spacing={1}
				>
					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="showPeriods"
							control={controlConfigs}
							defaultValue={configs.showPeriods}
							options={[{
								label: translate("Todos"),
								value: "T"
							},{
								label: translate("Acumulado"),
								value: "A"
							}]}
							label={translate("Mostrar períodos")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, showPeriods: e.value }))}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="divider"
							control={controlConfigs}
							defaultValue={configs.divider}
							options={[{
								label: "1",
								value: "1"
							},{
								label: "1000",
								value: "1000"
							},{
								label: "1000000",
								value: "1000000"
							}]}
							label={translate("Divisor dos valores")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, divider: e.value }))}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="listItens"
							control={controlConfigs}
							defaultValue={configs.listItens}
							options={[{
								label: translate("Somente com valores"),
								value: "V"
							},{
								label: translate("Todos"),
								value: "T"
							}]}
							label={translate("Listar itens")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, listItens: e.value }))}
						/>
					</RCCGrid>

					<RCCGrid
						container 
						item
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Select 
							name="variation"
							control={controlConfigs}
							defaultValue={configs.variation}
							options={[{
								label: translate("Planejado x Realizado"),
								value: "PR"
							},{
								label: translate("Realizado x Ano anterior"),
								value: "RL"
							},{
								label: translate("Planejado x Ano anterior"),
								value: "PL"
							}]}
							label={translate("Variação")}
							onChangeCapture={(e) => setConfigs((prevState) => ({ ...prevState, variation: e.value }))}
						/>
					</RCCGrid>
				</RCCGrid>

				{coinConversion && (
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={3}
							lg={3}
						>
							<LovCoins 
								name="coinPlanned"
								label={translate("Moeda planejado")}
								control={controlConfigs}
								defaultValue={configs.coinPlanned !== null ? configs.coinPlanned.toString() : undefined}
								isClearable
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ 
									...prevState, 
									coinPlanned: e != null ? e.value : null,
									coinPlannedInitials: e != null ? e.extendedProps.moa001Sigla : null  
								}))}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={3}
							lg={3}
						>
							<LovCoins 
								name="coinRealized"
								label={translate("Moeda realizado")}
								control={controlConfigs}
								defaultValue={configs.coinRealized !== null ? configs.coinRealized.toString() : undefined}
								isClearable
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ 
									...prevState, 
									coinRealized: e != null ? e.value : null,
									coinRealizedInitials: e != null ? e.extendedProps.moa001Sigla : null 
								}))}
							/>
						</RCCGrid>
					</RCCGrid>
				)}

				<RCCGrid
					container
					item
					spacing={1}
				>

					<RCCGrid
						container
						item
						direction="column"
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Title color="purple">
							{translate("Coluna")}
						</Title>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showColumnPlanned" 
								label={translate("Planejado")} 
								register={registerConfigs} 
								defaultChecked={configs.showColumnPlanned === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showColumnPlanned: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>
                        
						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showColumnRealized" 
								label={translate("Realizado")} 
								register={registerConfigs} 
								defaultChecked={configs.showColumnRealized === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showColumnRealized: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showColumnLastYear" 
								label={translate("Ano anterior")} 
								register={registerConfigs} 
								defaultChecked={configs.showColumnLastYear === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showColumnLastYear: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>
                        
					</RCCGrid>

					<RCCGrid
						container
						item
						direction="column"
						xs={12}
						sm={6}
						md={3}
						lg={3}
					>
						<Title color="purple">
							{translate("Variação")}
						</Title>
						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showVariationValue" 
								label={`${translate("Variação")} $`} 
								register={registerConfigs} 
								defaultChecked={configs.showVariationValue === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showVariationValue: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
						>
							<RCCSwitch 
								name="showVariationPercent" 
								label={`${translate("Variação")} %`} 
								register={registerConfigs} 
								defaultChecked={configs.showVariationPercent === "S"}
								labelPosition="end"
								onChangeCapture={(e: any) => setConfigs((prevState) => ({ ...prevState, showVariationPercent: e.target.checked ? "S" : "N" }))}
							/>
						</RCCGrid>

					</RCCGrid>

				</RCCGrid>
                       
			</ConfigsContainer>
			
			<RCCGrid
				container 
				item
			>
				<RCCSelectOptionsCard
					name="budget"
					defaultValue={budgetTypeSelected}
					options={options}
					label={translate("Orçamento")}
					onChange={(value) => {
						setBudgetTypeSelected(value as BudgetType);
					}}
				/>
			</RCCGrid>

			{/* Groupers Select -> Origin -> Delayed */}
			<form onSubmit={handleSubmitFormOptions(submitFormOptions)} style={{marginBottom: "1rem"}}>
				<ConfigsOptions 
					budgetType={budgetTypeSelected}
					controlFormOptions={controlFormOptions}
					registerFormOptions={registerFormOptions}
					setValueFormOptions={setValueFormOptions}
					budgetPlanId={Number(params.budgetPlanId)}
					subsidiary={objQueryParams?.subsidiary ? objQueryParams.subsidiary.toString() : undefined}
					yearConsult={yearConsult()}
					lov1={objQueryParams?.lov1 ? objQueryParams.lov1.toString() : undefined}
					lov2={objQueryParams?.lov2 ? objQueryParams.lov2.toString() : undefined}
					budgetUsesApportionment={budgetUsesApportionment}
					isLoading={loading}
					setSubsidiaryTitle={(label: string) => setSubsidiaryTitle(label)}
					buttonExport={dataTable && dataTable.length > 0 && (
						<RCCTooltip
							text={translate("Exportar")}
							color="purple"
						>
							<RCCButton
								color="purple"
								icon={{
									name: "download"
								}}
								layout="isRounded"
								size="md"
								loadingText=""
								loading={loadingExport}
								onClick={exportStructure}
							/>
						</RCCTooltip>
					)}
					hasConversionValueEqualZero={hasConversionValueEqualZero}
				/>
			</form>				
			
			<ExportListContainer
				ref={exportListContainerRef}
				classToBreakLineInsideSameColumn="break-columns-in-csv-here"
				style={{ position: "relative", zIndex: 0 }}
			>
				{Table}
			</ExportListContainer>

			<ButtonScrollTo
				minHeightToShowButton={400}
			/>
		</>
	);
};

export default ConsultValues;
